import { Class, Context } from "../base"

class Ref<T> {
    private _type: Class<T>;
    private _uuid: string;

    constructor(type:Class<T>, uuid:string) {
        if (!uuid) {
            throw new Error("Empty reference");
        }
        this._type = type;
        this._uuid = uuid;
    }

    get type() { return this._type; }
    get uuid() { return this._uuid; }

    obj() : T {
        const finder = Context.getInstance().getFinder(this._type);
        const value = finder.findById(this._uuid);
        if (!value) {
            throw new Error(`Invalid Reference ${this._uuid} for type ${this._type}`);
        }
        return value;
    }

    equals(ref:Ref<T>) {
        return this._uuid === ref.uuid;
    }
}

class CachingRef<T> extends Ref<T> {

    private _value : T | undefined = undefined;
    private _refresh: boolean = true;
    private _registered : boolean = false;

    private static _refs : Record<string, any> = {};

    constructor(type:Class<T>, uuid:string, value?:T) {
        super(type, uuid);
        this._value = value;
        this._refresh = (value === undefined);
    }

    static of<T>(type:Class<T>, uuid:string, value?:T) : CachingRef<T> {
        const existing = CachingRef._refs[uuid];
        if (existing) {
            return existing;
        }
        const ref = new CachingRef(type, uuid, value);
        CachingRef._refs[uuid] = ref;
        return ref;
    }

    refresh() {
        this._refresh = true;
    }

    private registerRefresh() {
        if (this._registered) {
            return
        }
        Context.getInstance().registerCacheRefresh(this.uuid, this.refresh.bind(this));
        this._registered = true;
    }

    obj() {
        if (this._value && !this._refresh) {
            return this._value;
        }
        this._value = super.obj();
        this._refresh = false;
        this.registerRefresh();
        return this._value;
    }
}

class IdentityRef<T> extends Ref<T> {
    constructor(type: Class<T>) {
        super(type, type.name + "Identity");
    }
}

export { Ref, CachingRef, IdentityRef }